// ============================================================================
// vpn/VpnConnectPanel.tsx — ГЛАВНЫЙ пользовательский сценарий: одна большая
// кнопка «Подключиться»/«Отключиться», понятное состояние и срок доступа.
// Всё техническое (QR, файл конфигурации) спрятано в «Расширенные».
// ============================================================================

import { useState } from 'react';
import { ChevronDown, ChevronUp, Clock, Loader2, Power, PowerOff, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { VpnConnectionDetails } from './VpnConnectionDetails';
import { vpnConnectionLabel, type VpnConnectionProfile } from './vpnTypes';

interface VpnConnectPanelProps {
  profile: VpnConnectionProfile;
  connected: boolean;
  busy: boolean;
  /** true — подключение открыто (сессия создана), false — не удалось. */
  onConnect: () => Promise<boolean>;
  /** true — подключение закрыто, false — сервер не подтвердил закрытие. */
  onDisconnect: () => Promise<boolean>;
}

export function VpnConnectPanel({ profile, connected, busy, onConnect, onDisconnect }: VpnConnectPanelProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const handleConnect = async () => {
    const ok = await onConnect();
    if (ok) setAdvancedOpen(true);
  };

  const handleDisconnect = async () => {
    const ok = await onDisconnect();
    if (ok) setAdvancedOpen(false);
  };

  const label = vpnConnectionLabel(profile);
  const unavailable = !profile.isActive && !connected;
  const stateText = connected
    ? 'Подключение открыто'
    : unavailable
      ? 'Отключено администратором'
      : 'Готово к подключению';
  const stateCls = connected
    ? 'text-green-600'
    : unavailable
      ? 'text-amber-600'
      : 'text-muted-foreground';

  return (
    <div className="flex h-full flex-col gap-3 rounded-lg border p-4">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ShieldCheck className="h-5 w-5" />
        </span>
        <div className="min-w-0">
          <div className="truncate text-base font-medium">{label}</div>
          <div className="text-xs text-muted-foreground">Защищённое подключение для ваших устройств</div>
        </div>
      </div>

      <div className="space-y-1 rounded-md bg-muted/30 p-3 text-xs">
        <div className="flex items-center gap-2">
          <span className={cn('h-2 w-2 rounded-full', connected ? 'bg-green-600' : unavailable ? 'bg-amber-600' : 'bg-muted-foreground/50')} />
          <span className="text-muted-foreground">Состояние:</span>
          <span className={cn('font-medium', stateCls)}>{stateText}</span>
        </div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <Clock className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <span className="text-muted-foreground">Срок действия доступа:</span>
          <span className="font-medium">бессрочно — пока действует ваша роль</span>
        </div>
      </div>

      {unavailable && (
        <div className="rounded-md border border-amber-600/40 bg-amber-600/5 p-2 text-xs text-amber-700">
          Администратор временно отключил это подключение. Если оно нужно — обратитесь к администратору.
        </div>
      )}

      {connected && !profile.isActive && (
        <div className="rounded-md border border-amber-600/40 bg-amber-600/5 p-2 text-xs text-amber-700">
          Администратор отключил подключение, но текущая сессия ещё открыта. Нажмите «Отключиться», чтобы завершить её.
        </div>
      )}

      <Button
        size="lg"
        variant={connected ? 'outline' : 'default'}
        className={cn('h-12 w-full text-base', connected && 'border-destructive/50 text-destructive hover:bg-destructive/5')}
        disabled={busy || unavailable}
        onClick={() => void (connected ? handleDisconnect() : handleConnect())}
      >
        {busy ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            {connected ? 'Отключаемся…' : 'Подключаемся…'}
          </>
        ) : connected ? (
          <>
            <PowerOff className="mr-2 h-4 w-4" /> Отключиться
          </>
        ) : (
          <>
            <Power className="mr-2 h-4 w-4" /> Подключиться
          </>
        )}
      </Button>

      <div className="text-xs text-muted-foreground">
        {connected
          ? 'Подключение открыто. Настройки для телефона или компьютера — в разделе «Расширенные».'
          : 'Нажмите «Подключиться» — мы подготовим настройки для этого устройства и откроем подключение.'}
      </div>

      <Button
        variant="ghost"
        size="sm"
        className="mt-auto justify-start px-0 text-xs"
        onClick={() => setAdvancedOpen((v) => !v)}
        aria-expanded={advancedOpen}
      >
        {advancedOpen ? <ChevronUp className="mr-1 h-3.5 w-3.5" /> : <ChevronDown className="mr-1 h-3.5 w-3.5" />}
        {advancedOpen ? 'Скрыть настройки для устройства' : 'Расширенные: QR и файл настроек'}
      </Button>

      {advancedOpen && (
        <VpnConnectionDetails profileCode={profile.code} profileName={label} />
      )}
    </div>
  );
}
